import {useMessage} from '@coolcolduk/ui-mui';
import {User as FirebaseUser} from 'firebase/auth';
import {useEffect, useState, useRef} from 'react';
import {submitContactMessage} from '../../api/contactMessage/submitContactMessage';
import createUserDoc from '../../api/user/createUserDoc';
import {env} from '../../config/env';
import {firebaseAuth} from '../../config/firebase';
import {useGetUser} from './useGetUser';

/**
 *
 * @returns undefined if loading, null if not logged in, user if logged in
 */
export function useAuth() {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null | undefined>(undefined);
  const {user, loading, error} = useGetUser({
    skip: !firebaseUser,
    uid: firebaseUser?.uid,
  });
  const {showMessage} = useMessage();
  const hasShownError = useRef(false);
  const createdUserForUid = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = firebaseAuth.onAuthStateChanged(async (usr: FirebaseUser | null) => {
      setFirebaseUser(usr);
      hasShownError.current = false;
      if (!usr) createdUserForUid.current = null;

      // Save Firebase UID to localStorage for dev/staging environments only
      if (env.isDev || env.isStage) {
        if (usr) {
          localStorage.setItem('uid', usr.uid);
          localStorage.setItem('email', usr.email || '');
        } else {
          localStorage.removeItem('uid');
          localStorage.removeItem('email');
        }
      }
    });

    return unsubscribe;
  }, []);

  // Show error message when getUser fails during login
  useEffect(() => {
    if (firebaseUser && error && !hasShownError.current && !loading) {
      hasShownError.current = true;
      showMessage(
        'Failed to load user data. Please try refreshing the page. If problem persists, please contact support.',
        6000,
        'error',
      );
    }
  }, [firebaseUser, error, loading, showMessage]);

  // New user: logged in but no user doc — create doc and notify admin
  useEffect(() => {
    if (!firebaseUser || loading || user != null) return;
    if (createdUserForUid.current === firebaseUser.uid) return;
    createdUserForUid.current = firebaseUser.uid;
    const email = firebaseUser.email ?? `${firebaseUser.uid}@newuser.local`;
    const name = (firebaseUser.displayName ?? email) || 'New user';
    createUserDoc(firebaseUser)
      .then(() => {
        return submitContactMessage({name, email, message: 'New user'}, firebaseUser.uid);
      })
      .catch((err) => {
        console.error('useAuth: failed to create user or send new-user contact', err);
        createdUserForUid.current = null;
      });
  }, [firebaseUser, loading, user]);

  if (firebaseUser === undefined || loading) {
    return undefined;
  }
  return firebaseUser === null ? null : user;
}
